import Icon, { IconType } from "src/components/Icon";
import classnames from 'classnames';
import * as React from 'react';
import Loader from "src/components/Loader";
import { getColorVar } from "src/helpers/getColor";
import { AvailableColors } from "src/interfaces/models";
import style from './Button.scss';

export enum BUTTON_ICON_POSITION {
  BEFORE,
  AFTER
}

//React.ButtonHTMLAttributes<HTMLButtonElement | HTMLLinkElement> &
export type Props = {
  className?: string,
  tag?: 'a' | 'button' | 'div' | React.FunctionComponent,
  icon?: IconType,
  iconPosition?: BUTTON_ICON_POSITION,
  iconClassName?: string,
  size?: 'sm' | 'md' | 'lg',
  outline?: boolean,
  transparent?: boolean,
  block?: boolean,
  cta?: boolean,
  color?: AvailableColors,
  loading?: boolean,
  children?: React.ReactNode,
  [buttonProps: string]: any,
};

const renderIcon = (icon: IconType, iconPosition: BUTTON_ICON_POSITION, position: BUTTON_ICON_POSITION, className: string) => {
  if (!icon || iconPosition !== position) return null;
  return <Icon icon={icon} className={classnames(style.icon, className)} />;
}

const Button: React.ForwardRefExoticComponent<Props> = React.forwardRef<any, Props>((props, ref) => {
  let {
    tag,
    icon,
    iconPosition,
    iconClassName,
    size,
    outline,
    transparent,
    block,
    cta,
    color,
    className,
    loading,
    children,
    ...buttonProps
  } = props;
  const onlyIcon = icon && !children;

  if (!color) color = 'primary';
  if (icon && !iconPosition) iconPosition = BUTTON_ICON_POSITION.BEFORE;

  const buttonClassName = classnames(
    style.container,
    onlyIcon && style.onlyIcon,
    iconPosition === BUTTON_ICON_POSITION.BEFORE && style.iconBefore,
    iconPosition === BUTTON_ICON_POSITION.AFTER && style.iconAfter,
    size && style[`size-${size}`],
    outline && style.outline,
    transparent && style.transparent,
    block && style.block,
    cta && style.cta,
    color.includes('fixed-') && style.fixed,
    className,
  );

  const tagStyle = React.useMemo(() => {
    if (color.includes('fixed-')) {
      return {
        '--button-color-b': getColorVar(`${color}`),
        '--button-color-m': getColorVar(`${color}`),
        '--button-color-t': getColorVar(`${color}`),
      }
    }
    return {
      '--button-color-b': getColorVar(`${color}-b`),
      '--button-color-m': getColorVar(`${color}-m`),
      '--button-color-t': getColorVar(`${color}-t`),
    }
  }, [color]);

  const Tag: React.ElementType = tag || 'button';

  return (
    <Tag
      ref={ref}
      className={buttonClassName}
      style={tagStyle} {...buttonProps}
      disabled={buttonProps.disabled || loading}
    >
      {renderIcon(icon, iconPosition, BUTTON_ICON_POSITION.BEFORE, iconClassName)}
      {children && <span className={style.text}>{children}</span>}
      {renderIcon(icon, iconPosition, BUTTON_ICON_POSITION.AFTER, iconClassName)}
      {loading && <span className={style.loader}><Loader size={20} /></span>}
    </Tag>
  );
});

Button.displayName = 'Button';

export default React.memo(Button);
